import { animate, stagger } from 'motion'
import { useInView } from 'motion/react'
import { splitText } from 'motion-plus'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useLocation } from 'react-router'
import useInvisibleUntilFontsLoaded
	from '#app/hooks/use-invisible-until-fonts-loaded.ts'
import { useIsMounted } from '#app/hooks/use-is-mounted.ts'

// Detect Safari browser
const isSafari = () => {
	if (typeof window === 'undefined') return false

	const userAgent = window.navigator.userAgent

	return (
		/Safari/.test(userAgent) &&
		!/Chrome/.test(userAgent) &&
		!/Chromium/.test(userAgent)
	)
}

export default function SplitText({
	as,
	element,
	delay = 0,
	...props
}: {
	as: keyof JSX.IntrinsicElements
	element?: 'lines' | 'words' | 'chars'
	delay?: number
} & React.ComponentPropsWithoutRef<any>) {
	const containerRef = useRef<HTMLDivElement>(null)
	const hasAnimatedRef = useRef(false)
	const animationRef = useRef<ReturnType<typeof animate> | null>(null)
	const mounted = useIsMounted()
	const safariDetected = useMemo(() => mounted && isSafari(), [mounted])

	const isInView = useInView(containerRef, {
		once: true,
	})

	const location = useLocation()

	// Check if component has line-clamp classes
	const hasLineClamp = useMemo(() => {
		return /(?:^|\s)(?:\w+:)?line-clamp-\d+/.test(props.className)
	}, [props.className])

	// Decide whether to skip animation (Safari - skip all animations to prevent any interference)
	const shouldSkipAnimation = safariDetected && hasLineClamp

	// Reset animation state on route change
	useEffect(() => {
		hasAnimatedRef.current = false
		animationRef.current?.stop()
		animationRef.current = null

		// Reset visibility for re-animation
		if (containerRef.current && !shouldSkipAnimation) {
			containerRef.current.style.visibility = 'hidden'
		}
	}, [location.key, shouldSkipAnimation])

	// Memoize animation config
	const animationConfig = useMemo(
		() => ({
			keyframes: { opacity: [0, 1], y: [20, 0] },
			options: {
				type: 'spring' as const,
				duration: 2 + delay,
				bounce: 0,
				delay: stagger(0.05, {
					startDelay: delay > 0 ? delay : undefined,
				}),
			},
		}),
		[delay],
	)

	// Memoize element selection logic
	const selectElements = useCallback(
		(chars: Element[], words: Element[], lines: Element[]) => {
			if (element) {
				return element === 'lines' ? lines : element === 'words' ? words : chars
			}
			return lines.length > 2 ? lines : words.length > 1 ? words : chars
		},
		[element],
	)

	const handleFontsLoaded = useCallback(() => {
		// Guard against multiple animations
		if (hasAnimatedRef.current || !isInView) return

		const target = containerRef.current?.querySelector(as)
		if (!target) return

		const { chars, words, lines } = splitText(target, {
			wordClass: 'inline-block whitespace-nowrap',
		})

		const elementsToAnimate = selectElements(chars, words, lines)

		hasAnimatedRef.current = true

		// Store animation reference for cleanup
		animationRef.current = animate(
			elementsToAnimate,
			animationConfig.keyframes,
			animationConfig.options,
		)
	}, [isInView, as, selectElements, animationConfig])

	// Only run once when fonts are loaded (but not for Safari + line-clamp)
	useInvisibleUntilFontsLoaded(
		containerRef,
		shouldSkipAnimation ? () => {} : handleFontsLoaded,
		[location.key],
	)

	// Cleanup animation on unmount
	useEffect(() => {
		return () => {
			animationRef.current?.stop()
		}
	}, [])

	const Comp = as || 'span'

	// If Safari + line-clamp, return the component directly without any wrappers
	if (shouldSkipAnimation) {
		return (
			<Comp className={props.className} {...props}>
				{props.children}
			</Comp>
		)
	}

	return (
		<span
			className={'invisible block will-change-[transform,opacity]'}
			ref={containerRef}
		>
			<Comp className={props.className} {...props}>
				{props.children}
			</Comp>
		</span>
	)
}